import { CBSocketClient, SocketClient } from "cbcore-ts"
import { YES } from "uicore-ts"
import {
    RHProcurement,
    RHProcurementOffer,
    RHProcurementPartOffer,
    RHProcurementStatus
} from "../../../models/prodModels"
import type {
    RHIncomingOfferEnvelope,
    RHIncomingOffersContentSnapshot,
    RHOfferCapabilities,
    RHOfferTeam,
    RHOutgoingOfferSummary,
    RHPartOfferInput
} from "../SocketClientFunctions"
import { RHCore } from "./RHCore"
import { RHFeatureFlags } from "./RHFeatureFlags"
import { RHSubscriptionBase } from "./RHSubscriptionBase"
import { referenceId } from "./RHReference"


// ============================================================================
// Enriched offer types
// ============================================================================

/**
 * An incoming offer as seen by the procurer.
 * `procurementId` is the ID of the associated procurement.
 * Resolve the full procurement at render time via:
 *   this.core.procurementStore.getProcurement(offer.procurementId)
 *
 * This is intentionally ID-only (not an embedded object) so that if the
 * procurement changes in procurementStore, the offer list view always
 * reads the live version rather than a stale snapshot. Views that display
 * procurement data from an incoming offer must subscribe to both offerStore
 * and procurementStore.
 */
export type RHIncomingOffer = Omit<RHProcurementOffer, "procurement" | "submittedAt"> & {
    /** Over the socket a date arrives as an ISO string; the list also keeps it that way so it sorts as text. */
    submittedAt?: Date | string
    procurementId: string
    /**
     * Per-procurement count of unseen offer.submitted notifications for the
     * current procurer, embedded by the server. Drives the "Uus pakkumus" dot
     * on the "Halda" action button (deadline-gated on the client). Cleared when
     * the procurer opens the procurement's offers tab.
     */
    unseenOfferNotificationCount: number
}

/**
 * An outgoing offer as seen by the offerer.
 * Carries the `requiresOffererAttention` flag computed by the server.
 */
/** An outgoing offer as listed: its submission time stays an ISO string so the list sorts it as text. */
export type RHOutgoingOffer = Omit<RHOutgoingOfferSummary, "submittedAt"> & { submittedAt?: Date | string }


/**
 * Access-aware result for a company's offer in one procurement. `offerId`
 * remains available when an offer exists but its detailed content is not
 * visible to the current company member.
 */
export interface RHOfferAccessSnapshot {
    offer: RHProcurementOffer | null
    offerId?: string
    capabilities?: RHOfferCapabilities
}


export interface RHSubmittedOfferAccessSnapshot extends RHOfferAccessSnapshot {
    closedOffers?: RHProcurementOffer[]
    partIdsRequiringResubmission: string[]
    /** Server cutoff captured by the same submitted-offer read that produced `offer`. */
    notificationSnapshotAt: string
}


// ============================================================================
// RHOfferStore
//
// Central store for offer list reads on both the procurer and offerer sides.
//
// ## Incoming offers (procurer)
// Fetches submitted offers via RetrieveIncomingOffersForCurrentProcurerUser.
// The procurement reference is stored as procurementId (string) only — not
// as an embedded object — so it cannot go stale when the procurement changes.
// Views showing both offer and procurement data must subscribe to both stores.
//
// ## Outgoing offers (offerer)
// Fetches via RetrieveOutgoingOffersForCurrentOffererUser. The server embeds
// requiresOffererAttention and offererSubmissionOutcome per offer. The procurement
// is populated with the fields the offer tables need: title, referenceNumber,
// parts, deadlineDate, estimatedCost, status and partWinnerOfferIds (the last three
// drive cancelled / winner derivation on the client).
//
// ## Subscription (inherited from RHSubscriptionBase)
//   store.subscribe(this, () => { ... })
//   store.unsubscribe(this)
//
// Subscribe in viewWillAppear BEFORE the first data read.
// Unsubscribe in viewWillDisappear.
// ============================================================================

export class RHOfferStore extends RHSubscriptionBase<(changedProcurementIds?: string[]) => void> {

    private _incomingOffers: RHIncomingOffer[] | undefined
    private _outgoingOffers: RHOutgoingOffer[] | undefined
    private _offerCapabilitiesByOfferId = new Map<string, RHOfferCapabilities>()
    private _offerCapabilitiesByProcurementId = new Map<string, RHOfferCapabilities>()

    /**
     * Per-thread subscribers. Keyed by subscriber object, value is the
     * procurementOfferId they are watching and the handler to call.
     * Used by OfferMessageThreadView to reload when a new message arrives.
     */
    private _threadSubscribers = new Map<
        object,
        { procurementOfferId: string, handler: () => void }
    >()


    constructor(private _core: RHCore) {
        super()
    }


    // =========================================================================
    // Per-thread subscriptions
    // Used by OfferMessageThreadView for live updates.
    // =========================================================================

    /**
     * Registers `handler` to be called whenever a new message arrives in the
     * thread for `procurementOfferId`. Each subscriber object may hold at most
     * one subscription — calling this again replaces the previous one.
     *
     * Subscribe in `wasAddedToViewTree`, unsubscribe in `wasRemovedFromViewTree`.
     */
    subscribeToThread(subscriber: object, procurementOfferId: string, handler: () => void) {
        this._threadSubscribers.set(subscriber, { procurementOfferId, handler })
    }

    /**
     * Removes any thread subscription held by `subscriber`.
     */
    unsubscribeFromThread(subscriber: object) {
        this._threadSubscribers.delete(subscriber)
    }

    /**
     * Called by RHCore when the server pushes `OfferMessageReceived`.
     * Notifies all subscribers watching the affected thread.
     */
    _onOfferMessageReceived(procurementOfferId: string) {
        for (const [_, subscription] of this._threadSubscribers) {
            if (subscription.procurementOfferId === procurementOfferId) {
                subscription.handler()
            }
        }
    }


    // =========================================================================
    // Read API
    // =========================================================================

    /**
     * Returns all submitted offers for all procurements owned by the current
     * user. Each offer carries a `procurementId` string — resolve the full
     * procurement at render time via procurementStore.getProcurement(id).
     *
     * Views that display procurement data alongside offer data must subscribe
     * to both offerStore and procurementStore so that changes to either
     * trigger a re-render.
     *
     * Cached after the first successful fetch. Call invalidateIncomingOffers()
     * to force a reload on the next access.
     */
    async getIncomingOffers(): Promise<RHIncomingOffer[]> {
        if (this._incomingOffers) {
            return this._incomingOffers
        }

        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        let rawOffers: RHProcurementOffer[]
        if (activeCompanyId) {
            rawOffers = (
                await SocketClient.RetrieveIncomingOffersForProcuringCompany(
                    { companyId: activeCompanyId },
                    CBSocketClient.completionPolicy.first
                )
            ).result
        }
        else {
            rawOffers = (
                await SocketClient.RetrieveIncomingOffersForCurrentProcurerUser()
            ).result
        }

        const resolved: RHIncomingOffer[] = rawOffers.map(offer => {
            const procurementId = offer.procurement?._id?.toString()
                ?? offer.procurement?.toString()
            const { procurement: _dropped, ...rest } = offer
            return { ...rest, procurementId } as RHIncomingOffer
        })

        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.getIncomingOffers()
        }
        this._incomingOffers = resolved
        return resolved
    }


    /**
     * Loads the incoming offers together with a server-issued audit cutoff.
     * Consumers that acknowledge offer.submitted notifications must use this
     * snapshot rather than a client timestamp, otherwise an offer submitted
     * concurrently with the read could be marked before it was rendered.
     */
    async retrieveIncomingOffersSnapshotForProcurement(
        procurementId: string,
        offerSelection: { offerIds?: string[], offererCompanyIds?: string[] } = {}
    ): Promise<RHIncomingOffersContentSnapshot> {
        const response = await SocketClient.RetrieveIncomingOffersForProcurement({
            procurementId,
            includeNotificationSnapshot: YES,
            ...offerSelection
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Pakkumuste laadimine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result) {
            throw new Error("Pakkumuste laadimine ebaõnnestus.")
        }
        if (Array.isArray(response.result)) {
            return { offers: response.result, notificationSnapshotAt: "" }
        }
        return response.result
    }


    // Asking for only the offers a screen shows keeps the server from decrypting every opened offer.
    async retrieveIncomingOfferWithIdForProcurement(
        offerId: string,
        procurementId: string
    ): Promise<RHProcurementOffer | undefined> {
        const offers = await this._retrieveOpenedIncomingOffersForProcurement(procurementId, { offerIds: [offerId] })
        return offers.find(offer => offer._id?.toString() === offerId)
    }


    async retrieveIncomingOffersFromCompaniesWithIdsForProcurement(
        companyIds: string[],
        procurementId: string
    ): Promise<RHProcurementOffer[]> {
        return this._retrieveOpenedIncomingOffersForProcurement(procurementId, { offererCompanyIds: companyIds })
    }


    async _retrieveOpenedIncomingOffersForProcurement(
        procurementId: string,
        offerSelection: { offerIds?: string[], offererCompanyIds?: string[] }
    ): Promise<RHProcurementOffer[]> {
        const snapshot = await this.retrieveIncomingOffersSnapshotForProcurement(procurementId, offerSelection)
        // Before offers open only envelopes arrive, which have no `offerer`; there is no offer content to return.
        return snapshot.offers.filter((offer): offer is RHProcurementOffer => "offerer" in offer)
    }

    async retrieveFilesDownloadForPartOffers(partOfferIds: string[]): Promise<{ url: string; fileName: string }> {
        const companyId = await this._core.companyAffiliationStore.activeCompanyIdAfterLoadingAffiliations()
        const response = await SocketClient.GetBulkDownloadUrl({ partOfferIds, companyId })
        const errorMessage = this._core.errorMessageFromResponse(response, "Failide allalaadimine ebaõnnestus.")
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.url) {
            throw new Error("Failide allalaadimine ebaõnnestus.")
        }
        return response.result
    }


    /**
     * Returns every offer submitted by the current user's company — including
     * withdrawn (rescinded) offers and offers on cancelled procurements; only
     * drafts are excluded. Each offer carries `requiresOffererAttention` and
     * `offererSubmissionOutcome` set by the server, and its `procurement` is
     * populated with the fields needed to derive cancelled / winner status.
     *
     * Cached after the first successful fetch. Call `invalidateOutgoingOffers()`
     * to force a reload on the next access.
     */
    async getOutgoingOffers(): Promise<RHOutgoingOffer[]> {
        if (this._outgoingOffers) {
            return this._outgoingOffers
        }

        const notificationStateGeneration = this._core.notificationStore.notificationStateGeneration
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        let offers: RHOutgoingOffer[]
        if (activeCompanyId) {
            offers = (
                await SocketClient.RetrieveOutgoingOffersForOffererCompany(
                    { companyId: activeCompanyId },
                    CBSocketClient.completionPolicy.first
                )
            ).result
        }
        else {
            offers = (
                await SocketClient.RetrieveOutgoingOffersForCurrentOffererUser()
            ).result
        }

        if (notificationStateGeneration !== this._core.notificationStore.notificationStateGeneration) {
            return this.getOutgoingOffers()
        }
        this._outgoingOffers = offers
        return offers
    }


    // =========================================================================
    // Server-push callbacks
    // Set by RHCore after construction. Never called directly by views.
    // =========================================================================

    /**
     * Called by RHCore when the server pushes `IncomingOffersDidChange`.
     * Invalidates the incoming cache and notifies subscribers — passing along
     * the IDs of the procurements the push concerns — so the procurer's offer
     * list view reloads automatically, and per-procurement views (e.g. the
     * offers evaluation view) can tell whether the change is even relevant
     * to what they are currently displaying.
     */
    _onIncomingOffersDidChange(changedProcurementIds: string[]) {
        this._incomingOffers = undefined
        this._notifySubscribers(changedProcurementIds)
    }

    /**
     * Called by RHCore when the server pushes `OutgoingOffersDidChange`.
     * Invalidates the outgoing cache and forwards the affected procurement IDs
     * so list and open detail views reload only the relevant state.
     */
    _onOutgoingOffersDidChange(changedProcurementIds: string[] = []) {
        this._outgoingOffers = undefined
        this._notifySubscribers(changedProcurementIds)
    }


    // =========================================================================
    // Per-procurement offer reads
    // =========================================================================

    /**
     * Returns the current company's submitted offer for a specific procurement,
     * or null if none exists. Not cached — always fetches fresh from the server,
     * because the submitted-offer state changes during the offer flow.
     */
    async getSubmittedOfferForProcurement(
        procurementId: string
    ): Promise<RHProcurementOffer | null> {
        return (await this.retrieveSubmittedOfferAccessSnapshotForProcurement(procurementId)).offer
    }


    async retrieveSubmittedOfferAccessSnapshotForProcurement(
        procurementId: string
    ): Promise<RHSubmittedOfferAccessSnapshot> {
        const message: {
            procurementId: string
            offererCompanyId?: string
        } = {
            procurementId
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.RetrieveSubmittedOfferForProcurement(message)
        if (!response.result) {
            const fallback = "Esitatud pakkumuse laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._rememberOfferCapabilities(
            procurementId,
            response.result.offer,
            response.result.capabilities,
            response.result.offerId
        )
        return {
            offer: response.result.offer,
            offerId: response.result.offerId,
            capabilities: response.result.capabilities,
            notificationSnapshotAt: response.result.notificationSnapshotAt,
            closedOffers: response.result.closedOffers ?? [],
            partIdsRequiringResubmission: response.result.partIdsRequiringResubmission
        }
    }

    /**
     * Returns the current company's draft offer for a specific procurement,
     * or null if none exists. Not cached — always fetches fresh, because the
     * draft is mutated during the offer-entry flow.
     */
    async getDraftOfferForProcurement(
        procurementId: string
    ): Promise<RHProcurementOffer | null> {
        return (await this.retrieveDraftOfferAccessSnapshotForProcurement(procurementId)).offer
    }


    async retrieveDraftOfferAccessSnapshotForProcurement(
        procurementId: string
    ): Promise<RHOfferAccessSnapshot> {
        const message: {
            procurementId: string
            offererCompanyId?: string
        } = {
            procurementId
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.RetrieveDraftOfferForProcurement(message)
        if (!response.result) {
            const fallback = "Ettevalmistamisel pakkumuse laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._rememberOfferCapabilities(
            procurementId,
            response.result.offer,
            response.result.capabilities,
            response.result.offerId
        )
        return {
            offer: response.result.offer,
            offerId: response.result.offerId,
            capabilities: response.result.capabilities
        }
    }


    async retrieveOfferCapabilities(offerId: string): Promise<RHOfferCapabilities> {
        const capabilities = (await SocketClient.RetrieveOfferCapabilities({ offerId })).result
        this._offerCapabilitiesByOfferId.set(offerId, capabilities)
        return capabilities
    }


    offerCapabilitiesForOfferId(offerId: string): RHOfferCapabilities | undefined {
        return this._offerCapabilitiesByOfferId.get(offerId)
    }


    offerCapabilitiesForProcurementId(procurementId: string): RHOfferCapabilities | undefined {
        return this._offerCapabilitiesByProcurementId.get(procurementId)
    }


    async retrieveOfferTeam(offerId: string): Promise<RHOfferTeam> {
        const response = await SocketClient.RetrieveOfferTeam({ offerId })
        if (!response.result?.success) {
            const fallback = "Pakkuja meeskonna laadimine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._rememberOfferTeamCapabilities(offerId, response.result.team)
        return response.result.team
    }


    async updateOfferTeam(
        offerId: string,
        team: {
            authorizedOffererUserIds: string[]
            observerOffererUserIds: string[]
        }
    ): Promise<RHOfferTeam> {
        const response = await SocketClient.UpdateOfferTeam({
            offerId,
            authorizedOffererUserIds: team.authorizedOffererUserIds,
            observerOffererUserIds: team.observerOffererUserIds
        })
        if (!response.result?.success) {
            const fallback = "Pakkuja meeskonna uuendamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateOfferTeamCaches()
        this._rememberOfferTeamCapabilities(offerId, response.result.team)
        return response.result.team
    }


    async changeOfferResponsibleUser(offerId: string, responsibleUserId: string): Promise<RHOfferTeam> {
        const response = await SocketClient.ChangeOfferResponsibleUser({
            offerId,
            responsibleUserId
        })
        if (!response.result?.success) {
            const fallback = "Pakkumuse vastutava isiku muutmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateOfferTeamCaches()
        this._rememberOfferTeamCapabilities(offerId, response.result.team)
        return response.result.team
    }


    async addCurrentUserToOfferTeam(offerId: string): Promise<RHOfferTeam> {
        const response = await SocketClient.AddCurrentUserToOfferTeam({
            offerId,
            role: "authorized"
        })
        if (!response.result?.success) {
            const fallback = "Pakkuja meeskonnaga liitumine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._invalidateOfferTeamCaches()
        this._rememberOfferTeamCapabilities(offerId, response.result.team)
        return response.result.team
    }


    // =========================================================================
    // Write API
    // =========================================================================

    /**
     * Upserts the company's draft offer for a procurement and returns the saved
     * draft. Invalidates offer and published/dashboard procurement state so list
     * views reflect the updated offer situation on next load.
     */
    async saveDraftOffer(
        procurementId: string,
        partOffers: import("../SocketClientFunctions").RHPartOfferInput[]
    ): Promise<RHProcurementOffer> {
        const message: {
            procurementId: string
            offererCompanyId?: string
            partOffers: import("../SocketClientFunctions").RHPartOfferInput[]
        } = {
            procurementId,
            partOffers
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.SaveDraftOffer(message)
        if (!response.result) {
            const fallback = "Pakkumuse salvestamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._rememberOfferCapabilities(procurementId, response.result.offer, response.result.capabilities)
        this.invalidateOutgoingOffers([procurementId])
        this._core.procurementStore.invalidatePublishedProcurements([procurementId])
        return response.result.offer
    }


    private _invalidateOfferTeamCaches() {
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
    }


    private _rememberOfferTeamCapabilities(offerId: string, team: RHOfferTeam) {
        if (!team.capabilities) {
            return
        }
        this._offerCapabilitiesByOfferId.set(offerId, team.capabilities)
    }

    /**
     * Transitions the company's draft offer to submitted status and returns
     * the submitted offer. Invalidates both the incoming and outgoing offers
     * caches so all list views see fresh data.
     */
    async submitOffer(offerId: string): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            offererCompanyId?: string
        } = {
            offerId
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.SubmitOffer(message)
        if (!response.result) {
            const fallback = "Pakkumuse esitamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        const offer = response.result.offer
        const procurementId = referenceId(offer.procurement) ?? ""
        this._rememberOfferCapabilities(procurementId, offer, response.result.capabilities)
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async deleteDraftOfferWithId(offerId: string, procurementId: string): Promise<void> {
        const message: { offerId: string; offererCompanyId?: string } = { offerId }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.DeleteDraftOffer(message)
        if (!response.result?.success) {
            const fallback = "Pakkumuse mustandi kustutamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._offerCapabilitiesByOfferId.delete(offerId)
        this._offerCapabilitiesByProcurementId.delete(procurementId)
        this.invalidateOutgoingOffers([procurementId])
        this._core.procurementStore.invalidatePublishedProcurements([procurementId])
    }


    private _rememberOfferCapabilities(
        procurementId: string,
        offer: RHProcurementOffer | null,
        capabilities?: RHOfferCapabilities,
        explicitOfferId?: string
    ) {
        if (!capabilities) {
            return
        }
        if (procurementId.length > 0) {
            this._offerCapabilitiesByProcurementId.set(procurementId, capabilities)
        }
        const offerId = explicitOfferId ?? (referenceId(offer?._id) ?? "")
        if (offerId.length > 0) {
            this._offerCapabilitiesByOfferId.set(offerId, capabilities)
        }
    }


    /**
     * Adds one part offer to an already-submitted offer package while the
     * procurement still permits another offer for that part.
     */
    async addSubmittedPartOffer(
        offerId: string,
        partOffer: RHPartOfferInput
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            partOffer: RHPartOfferInput
            offererCompanyId?: string
        } = {
            offerId,
            partOffer
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.AddSubmittedPartOffer(message)
        if (!response.result) {
            const fallback = "Pakkumuse lisamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }

    /**
     * Updates one submitted part offer without cancelling the other part offers
     * in the same procurement offer package.
     */
    async updateSubmittedPartOffer(
        offerId: string,
        partOfferId: string,
        partOffer: RHPartOfferInput
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            partOfferId: string
            partOffer: RHPartOfferInput
            offererCompanyId?: string
        } = {
            offerId,
            partOfferId,
            partOffer
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.UpdateSubmittedPartOffer(message)
        if (!response.result) {
            const fallback = "Pakkumuse muutmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async modifySubmittedOfferAfterAmendment(
        offerId: string,
        amendmentId: string,
        partOffers: RHPartOfferInput[]
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            amendmentId: string
            partOffers: RHPartOfferInput[]
            offererCompanyId?: string
        } = {
            offerId,
            amendmentId,
            partOffers
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.ModifySubmittedOfferAfterAmendment(message)
        if (!response.result?.success || !response.result.offer) {
            const fallback = "Pakkumuse muutmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async confirmOfferForProcurementAmendment(
        offerId: string,
        amendmentId: string
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            amendmentId: string
            offererCompanyId?: string
        } = {
            offerId,
            amendmentId
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.ConfirmOfferForProcurementAmendment(message)
        if (!response.result?.success || !response.result.offer) {
            const fallback = "Pakkumuse kinnitamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async withdrawOfferDueToProcurementAmendment(
        offerId: string,
        amendmentId: string,
        explanation: string
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            amendmentId: string
            explanation: string
            offererCompanyId?: string
        } = {
            offerId,
            amendmentId,
            explanation
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.WithdrawOfferDueToProcurementAmendment(message)
        if (!response.result?.success || !response.result.offer) {
            const fallback = "Pakkumuse tagasivõtmine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async objectToProcurementAmendment(
        offerId: string,
        amendmentId: string,
        explanation: string
    ): Promise<RHProcurementOffer> {
        const message: {
            offerId: string
            amendmentId: string
            explanation: string
            offererCompanyId?: string
        } = {
            offerId,
            amendmentId,
            explanation
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.ObjectToProcurementAmendment(message)
        if (!response.result?.success || !response.result.offer) {
            const fallback = "Vastuväite salvestamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    async openProcurementOffers(
        procurementId: string
    ): Promise<{
        success: boolean
        procurement: RHProcurement
        openedAt: Date
        notifiedCount: number
        alreadyOpened?: boolean
    }> {
        const response = await SocketClient.OpenProcurementOffers({ procurementId })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Pakkumuste avamine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success) {
            throw new Error("Pakkumuste avamine ebaõnnestus.")
        }
        this._incomingOffers = undefined
        this._notifySubscribers([procurementId])
        return response.result
    }


    async updatePartOfferEvaluation(input: {
        partOfferId: string
        meetsRequirements?: boolean
        evaluationNotes: string
        decision: string
    }): Promise<RHProcurementPartOffer> {
        const response = await SocketClient.UpdatePartOfferEvaluation(
            input,
            CBSocketClient.completionPolicy.directOnly
        )
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Hindamise salvestamine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success) {
            throw new Error("Hindamise salvestamine ebaõnnestus.")
        }
        this._incomingOffers = undefined
        const procurementId = referenceId(response.result.partOffer.procurement) ?? ""
        let changedProcurementIds: string[] | undefined
        if (procurementId.length > 0) {
            changedProcurementIds = [procurementId]
        }
        this._notifySubscribers(changedProcurementIds)
        return response.result.partOffer
    }


    async setProcurementPartWinner(procurementId: string, partOfferId: string): Promise<void> {
        const response = await SocketClient.SetProcurementPartWinner({
            procurementId,
            partOfferId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Pakkumuse valiku salvestamine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success) {
            throw new Error("Pakkumuse valiku salvestamine ebaõnnestus.")
        }
        this._incomingOffers = undefined
        this._notifySubscribers([procurementId])
    }


    async finalizePartEvaluationAndNotify(
        procurementId: string,
        partId: string,
        expectedWinningPartOfferId: string | null
    ): Promise<{
        success: boolean
        message: string
        notifiedCount: number
        allPartsFinalized: boolean
        finalizedPartIds: string[]
        newProcurementStatus: RHProcurementStatus
    }> {
        const response = await SocketClient.FinalizePartEvaluationAndNotify({
            procurementId,
            partId,
            expectedWinningPartOfferId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Osa hindamise lõpetamine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success) {
            throw new Error("Osa hindamise lõpetamine ebaõnnestus.")
        }
        this._incomingOffers = undefined
        this._notifySubscribers([procurementId])
        return response.result
    }


    /** Gives up on further reconciliation rounds for a part currently locked pending one. */
    async abandonReconciliationForPart(
        procurementId: string,
        partId: string
    ): Promise<{
        success: boolean
        message: string
        notifiedCount: number
        allPartsFinalized: boolean
        finalizedPartIds: string[]
        newProcurementStatus: RHProcurementStatus
    }> {
        const response = await SocketClient.AbandonReconciliationForPart({
            procurementId,
            partId
        })
        const errorMessage = this._core.errorMessageFromResponse(
            response,
            "Täpsustamisest loobumine ebaõnnestus."
        )
        if (errorMessage) {
            throw new Error(errorMessage)
        }
        if (!response.result?.success) {
            throw new Error("Täpsustamisest loobumine ebaõnnestus.")
        }
        this._incomingOffers = undefined
        this._notifySubscribers([procurementId])
        return response.result
    }

    /**
     * Cancels one submitted part offer. Returns the updated submitted offer, or
     * null when the cancelled part was the last active part offer.
     */
    async rescindSubmittedPartOffer(
        offerId: string,
        partOfferId: string
    ): Promise<RHProcurementOffer | null> {
        const message: {
            offerId: string
            partOfferId: string
            offererCompanyId?: string
        } = {
            offerId,
            partOfferId
        }
        const activeCompanyId = await this._activeCompanyIdForExplicitCompanyCalls()
        if (activeCompanyId) {
            message.offererCompanyId = activeCompanyId
        }
        const response = await SocketClient.RescindSubmittedPartOffer(message)
        if (!response.result) {
            const fallback = "Pakkumuse tühistamine ebaõnnestus."
            throw new Error(this._core.errorMessageFromResponse(response, fallback) ?? fallback)
        }
        this._outgoingOffers = undefined
        this._incomingOffers = undefined
        this._notifySubscribers()
        return response.result.offer
    }


    // =========================================================================
    // Cache invalidation
    // =========================================================================

    /**
     * Clears the incoming offers cache and notifies subscribers.
     * Call from `viewWillAppear` in `IncomingOffersViewController` so the list
     * always reflects the latest server state when the view becomes visible.
     */
    invalidateIncomingOffers() {
        this._incomingOffers = undefined
        this._notifySubscribers()
    }


    /**
     * Updates only the notification metadata embedded in an already-cached
     * incoming-offers list. This deliberately does not notify offer-content
     * subscribers: RHNotificationStore publishes the acknowledgement state
     * immediately afterwards, while waking RHOffersEvaluationView here would
     * cause an unnecessary offers refetch for a metadata-only change.
     */
    applyIncomingOfferNotificationCounts(
        countsByProcurementId: Record<string, { unseenOfferNotificationCount: number }>
    ) {
        if (!this._incomingOffers) {
            return
        }
        for (const incomingOffer of this._incomingOffers) {
            const counts = countsByProcurementId[incomingOffer.procurementId]
            if (!counts) {
                continue
            }
            incomingOffer.unseenOfferNotificationCount = Math.max(
                0,
                Number(counts.unseenOfferNotificationCount) || 0
            )
        }
    }


    /** Metadata-only counterpart for the cached offerer-side list. */
    applyOutgoingOfferNotificationCounts(
        countsByProcurementId: Record<string, { unseenOfferNotificationCount: number }>
    ) {
        if (!this._outgoingOffers) {
            return
        }
        for (const outgoingOffer of this._outgoingOffers) {
            const procurementId = referenceId(outgoingOffer.procurement) ?? ""
            const counts = countsByProcurementId[procurementId]
            if (!counts) {
                continue
            }
            outgoingOffer.requiresOffererAttention =
                (Number(counts.unseenOfferNotificationCount) || 0) > 0
        }
    }


    /**
     * Clears the outgoing offers cache and notifies subscribers.
     * Call from `viewWillAppear` in `OutgoingOffersViewController`.
     */
    invalidateOutgoingOffers(changedProcurementIds: string[] = []) {
        this._outgoingOffers = undefined
        this._notifySubscribers(changedProcurementIds)
    }


    /** Resets all cached offers. Call on logout so the next login never sees a stale user's data. */
    clear() {
        this._incomingOffers = undefined
        this._outgoingOffers = undefined
        this._offerCapabilitiesByOfferId.clear()
        this._offerCapabilitiesByProcurementId.clear()
        this._notifySubscribers()
    }


    async _activeCompanyIdForExplicitCompanyCalls(): Promise<string | undefined> {
        if (RHFeatureFlags.multiCompanyAffiliationIsEnabled !== YES) {
            return undefined
        }
        return this._core.companyAffiliationStore.activeCompanyIdAfterLoadingAffiliations()
    }

}
